import { MapMouseEvent } from 'mapbox-gl';

import { GeoArcEditSessionService } from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { nearestPathIndex, viaInsertionIndex } from '@core/utils/geo-polyline.utils';

import {
  DRAG_THRESHOLD_PX,
  ScreenPoint,
  isDragGesture
} from '@features/components/visualization/mapbox/map-elements/map-arc-editor.utils';
import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';

const PATH_SOURCE = 'geo-arc-edit-path';
const PATH_LAYER = 'geo-arc-edit-path-layer';
const PATH_HIT_LAYER = 'geo-arc-edit-path-hit-layer';
const VIA_SOURCE = 'geo-arc-vias';
const VIA_LAYER = 'geo-arc-vias-layer';
const GHOST_SOURCE = 'geo-arc-drag-ghost';
const GHOST_LAYER = 'geo-arc-drag-ghost-layer';
const PATH_COLOR = '#0f6b73';
const VIA_COLOR = '#0f6b73';
const VIA_HALO_COLOR = '#ffffff';
/** Grey, not the via teal: the point is a destination, not a via the provider has routed. */
const GHOST_COLOR = '#9ca3af';

/**
 * Owns both the pointer gestures of an arc edit session and the live rendering of the path
 * under edit. MapRoutes withholds that same arc from its own source for the session's
 * duration, so nothing else paints or hit-tests it — this element has to do both, the way
 * mapbox-gl-draw does for a geographic area under edit.
 */
export class MapArcEditor implements MapElement {
  private isAttached = false;
  private draggingViaIndex: number | null = null;
  private grabbedPathIndex: number | null = null;
  private pressedAt: ScreenPoint | null = null;
  private ghostAt: GeoLocation | null = null;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly session: GeoArcEditSessionService
  ) {}

  init(): void {
    // Sources and layers are added and removed with the session.
  }

  registerEvents(): void {
    // Handlers are attached by syncSession, driven from a component effect.
  }

  destroy(): void {
    this.detach();
  }

  /** Called from a component effect whenever the session's uid, path or vias change. */
  syncSession(): void {
    if (this.session.isActive() && !this.isAttached) {
      this.attach();
    } else if (!this.session.isActive() && this.isAttached) {
      this.detach();
    } else if (this.isAttached) {
      this.renderPath();
      this.renderVias();
    }
  }

  private attach(): void {
    this.map.addSource(PATH_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    // Visible, heavier and more opaque than MapRoutes' 0.5-opacity read-only line, so an
    // arc being edited reads differently from one merely being viewed.
    this.map.addLayer({
      id: PATH_LAYER,
      type: 'line',
      source: PATH_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': PATH_COLOR,
        'line-width': 5,
        'line-opacity': 1
      }
    });
    // A wide, invisible twin of PATH_LAYER: the hit target dragging re-routes. MapRoutes'
    // own invisible layer can't serve this since its source withholds the arc under edit.
    this.map.addLayer({
      id: PATH_HIT_LAYER,
      type: 'line',
      source: PATH_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': PATH_COLOR,
        'line-width': 30,
        'line-opacity': 0
      }
    });

    this.map.addSource(VIA_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    this.map.addLayer({
      id: VIA_LAYER,
      type: 'circle',
      source: VIA_SOURCE,
      paint: {
        'circle-radius': 6,
        'circle-color': VIA_COLOR,
        'circle-stroke-width': 2,
        'circle-stroke-color': VIA_HALO_COLOR
      }
    });

    this.map.addSource(GHOST_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    // Added last so it paints above the vias: while a via is being moved the ghost has to
    // stay readable over the one it will replace.
    this.map.addLayer({
      id: GHOST_LAYER,
      type: 'circle',
      source: GHOST_SOURCE,
      paint: {
        'circle-radius': 6,
        'circle-color': GHOST_COLOR,
        'circle-stroke-width': 2,
        'circle-stroke-color': VIA_HALO_COLOR
      }
    });

    // Removing a via is a double-click; without this it also zooms the map.
    this.map.doubleClickZoom.disable();

    // A via always sits ON the path, so pressing one fires both onViaMouseDown and
    // onPathMouseDown. Which of the two wins is decided in onMouseUp, whose `else if`
    // prefers the via branch — moving the via the user grabbed rather than inserting a
    // second one on top of it.
    this.map.on('mousedown', VIA_LAYER, this.onViaMouseDown);
    this.map.on('dblclick', VIA_LAYER, this.onViaDoubleClick);
    this.map.on('mousedown', PATH_HIT_LAYER, this.onPathMouseDown);
    // Map-wide rather than layer-scoped: a drag leaves the 30 px hit strip within a few
    // pixels of travel, and the ghost has to keep following the cursor after it does.
    this.map.on('mousemove', this.onMouseMove);
    this.map.on('mouseup', this.onMouseUp);

    this.isAttached = true;
    this.renderPath();
    this.renderVias();
  }

  private detach(): void {
    if (!this.isAttached) {
      return;
    }
    this.map.off('mousedown', VIA_LAYER, this.onViaMouseDown);
    this.map.off('dblclick', VIA_LAYER, this.onViaDoubleClick);
    this.map.off('mousedown', PATH_HIT_LAYER, this.onPathMouseDown);
    this.map.off('mousemove', this.onMouseMove);
    this.map.off('mouseup', this.onMouseUp);

    if (this.map.getLayer(GHOST_LAYER)) {
      this.map.removeLayer(GHOST_LAYER);
    }
    if (this.map.getSource(GHOST_SOURCE)) {
      this.map.removeSource(GHOST_SOURCE);
    }
    if (this.map.getLayer(VIA_LAYER)) {
      this.map.removeLayer(VIA_LAYER);
    }
    if (this.map.getSource(VIA_SOURCE)) {
      this.map.removeSource(VIA_SOURCE);
    }
    if (this.map.getLayer(PATH_HIT_LAYER)) {
      this.map.removeLayer(PATH_HIT_LAYER);
    }
    if (this.map.getLayer(PATH_LAYER)) {
      this.map.removeLayer(PATH_LAYER);
    }
    if (this.map.getSource(PATH_SOURCE)) {
      this.map.removeSource(PATH_SOURCE);
    }
    this.map.doubleClickZoom.enable();
    // A session can end mid-drag (button still down, no mouseup yet), which tears down the
    // listener that would otherwise have re-enabled panning — so it's restored here too.
    this.map.dragPan.enable();

    this.draggingViaIndex = null;
    this.grabbedPathIndex = null;
    this.pressedAt = null;
    this.ghostAt = null;
    this.isAttached = false;
  }

  private renderPath(): void {
    const source = this.map.getSource(PATH_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    const path = this.session.path();
    // MapRoutes withholds the arc under edit for the whole session — including a brand-new
    // arc, which starts with no path at all — so this is the only thing left that can draw
    // something to grab. Falling back to the endpoints gives a new arc a placeholder to
    // drag its first via out of, exactly like a stored path.
    const endpoints = this.session.endpoints();
    const coordinates: GeoJSON.Position[] | null =
      path.length >= 2
        ? path.map((point) => [point.lng, point.lat])
        : endpoints
          ? [
              [endpoints.tail.lng, endpoints.tail.lat],
              [endpoints.head.lng, endpoints.head.lat]
            ]
          : null;

    // A per-layer paint change is enough since this layer only ever holds one feature.
    // The dash marks a placeholder straight line the provider has not routed yet, so it
    // never reads as a computed road.
    this.map.setPaintProperty(
      PATH_LAYER,
      'line-dasharray',
      path.length >= 2 ? undefined : [2, 2]
    );

    source?.setData({
      type: 'FeatureCollection',
      features: coordinates
        ? [
            {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates }
            }
          ]
        : []
    });
  }

  /**
   * The point the release would route through, or nothing at all. Only the marker moves during
   * a drag: the path and the vias are the provider's last word and stay put until it answers
   * again.
   */
  private renderGhost(at: GeoLocation | null): void {
    // mousemove fires for the whole session, not just during a drag, and every setData costs
    // the source a reparse. Clearing what is already clear is the common case by far.
    if (!at && !this.ghostAt) {
      return;
    }
    this.ghostAt = at;
    const source = this.map.getSource(GHOST_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    source?.setData({
      type: 'FeatureCollection',
      features: at
        ? [
            {
              type: 'Feature',
              properties: {},
              geometry: { type: 'Point', coordinates: [at.lng, at.lat] }
            }
          ]
        : []
    });
  }

  private renderVias(): void {
    const source = this.map.getSource(VIA_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    source?.setData({
      type: 'FeatureCollection',
      features: this.session.vias().map((via, index) => ({
        type: 'Feature',
        id: index,
        properties: { index },
        geometry: { type: 'Point', coordinates: [via.lng, via.lat] }
      }))
    });
  }

  private readonly onViaMouseDown = (event: MapMouseEvent): void => {
    // A gesture started while a request is in flight would be dropped by the session with
    // no feedback (applyVias returns silently on isComputing), so it must never arm here.
    if (this.session.isComputing()) {
      return;
    }
    event.preventDefault();
    const index = event.features?.[0]?.properties?.['index'];
    this.draggingViaIndex = typeof index === 'number' ? index : null;
    this.pressedAt = event.point;
    this.map.dragPan.disable();
  };

  private readonly onPathMouseDown = (event: MapMouseEvent): void => {
    // Same reasoning as onViaMouseDown: dropping a gesture mid-request would look like a
    // handle silently snapping back, with nothing telling the user why.
    if (this.session.isComputing()) {
      return;
    }
    event.preventDefault();
    this.grabbedPathIndex = nearestPathIndex(this.session.path(), toGeoLocation(event));
    this.pressedAt = event.point;
    this.map.dragPan.disable();
  };

  private readonly onMouseMove = (event: MapMouseEvent): void => {
    // Gated on the same threshold the release is: a press that has not travelled far enough
    // to re-route must not advertise a waypoint it will not place.
    const dragging =
      this.pressedAt !== null &&
      isDragGesture(this.pressedAt, event.point, DRAG_THRESHOLD_PX);
    this.renderGhost(dragging ? toGeoLocation(event) : null);
  };

  private readonly onViaDoubleClick = (event: MapMouseEvent): void => {
    event.preventDefault();
    const index = event.features?.[0]?.properties?.['index'];
    if (typeof index === 'number') {
      void this.session.removeVia(index);
    }
  };

  private readonly onMouseUp = (event: MapMouseEvent): void => {
    const dropped = toGeoLocation(event);
    this.renderGhost(null);
    // Only travel makes a press a drag. A stationary press is a click — and a double click
    // delivers two of them before its dblclick, each of which would otherwise spend a
    // provider request and leave one in flight for the removal to be refused for.
    const dragged =
      this.pressedAt !== null &&
      isDragGesture(this.pressedAt, event.point, DRAG_THRESHOLD_PX);

    if (dragged && this.draggingViaIndex !== null) {
      void this.session.moveVia(this.draggingViaIndex, dropped);
    } else if (dragged && this.grabbedPathIndex !== null) {
      const index = viaInsertionIndex(
        this.session.path(),
        this.session.vias(),
        this.grabbedPathIndex
      );
      void this.session.insertVia(dropped, index);
    }

    this.draggingViaIndex = null;
    this.grabbedPathIndex = null;
    this.pressedAt = null;
    this.map.dragPan.enable();
  };
}

function toGeoLocation(event: MapMouseEvent): GeoLocation {
  return { lat: event.lngLat.lat, lng: event.lngLat.lng };
}
